/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * ExportParticipants — the CSV / Excel buttons on the History participant list
 * ------------------------------------------------------------------------
 * Exports exactly what the list shows: the same status chip, passcode search
 * and activity. One row per pass that isn't cancelled; the columns are
 * PARTICIPANT_EXPORT_COLUMNS (name, email, phone, passcode, activity, status).
 *
 * Requests, all on existing endpoints: one to start the export job
 * (/table/export/ with `columns`), one every 1.5 s to read the job's status
 * (/jobs/list/, the caller's own jobs only), and one to download the finished
 * file (/exports/download/, streamed in chunks). Nothing runs until a button
 * is pressed.
 */

import React, { useEffect, useRef, useState } from 'react';
import { checkInApi } from '../../../hooks/useCheckInDesk';
import { requestParticipantExport, fetchExportJob, downloadExportFile } from './checkInApi';

const POLL_MS = 1500;
const MAX_POLLS = 120; // three minutes
const FORMATS = [
  { id: 'csv', label: 'CSV' },
  { id: 'xlsx', label: 'Excel' },
];

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// /** "E2E Audit Run 2026" → "e2e-audit-run-2026". */
// function slug(text) {
//   return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
// }

/**
 * What the file is, in words: the event, then what the list was narrowed to.
 * "Coastal Ultra 2026 - Participants (Checked in, Coastal 21K)". Sent as the
 * export's `file_name` (the server turns it into a safe stored name) and used
 * for the name the browser saves.
 */
export function exportTitle(eventName, scopeLabels = []) {
  const event = String(eventName || '').trim() || 'Event';
  const scope = scopeLabels.filter(Boolean);
  return `${event} - Participants${scope.length ? ` (${scope.join(', ')})` : ''}`;
}

/**
 * The name the organizer's browser saves the file under:
 * "Coastal Ultra 2026 - Participants (Checked in, Coastal 21K) - 2026-09-28.xlsx".
 * Characters no operating system accepts in a filename are dropped.
 */
export function exportFileName(eventName, format, now = new Date(), scopeLabels = []) {
  const day = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
  // eslint-disable-next-line no-control-regex
  const safe = (text) => String(text || '').replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim();
  // A very long event name is shortened; what the file holds is kept whole.
  const title = safe(exportTitle(safe(eventName).slice(0, 100).trim(), scopeLabels.map(safe)))
    .slice(0, 150)
    .trim();
  return `${title} - ${day}.${format}`;
}

function saveBlob(blob, name) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked a moment later so the browser has picked the file up first.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

function ExportParticipants({
  eventId, eventName, statuses, search, registrationIds, scopeLabels = [],
  disabled = false, empty = false, pollMs = POLL_MS,
}) {
  const [state, setState] = useState({ phase: 'idle', format: null, message: '' });
  const alive = useRef(true);
  // Set on every mount, not only the first: React's StrictMode mounts,
  // unmounts and mounts again in development, and a flag left false by that
  // first unmount would make every export ignore its own answer.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // A different event, filter or activity makes a finished or failed message
  // stale; one that is still running keeps going and saves what was asked.
  const scopeKey = [
    eventId, (statuses || []).join(','), search || '', registrationIds ? registrationIds.join(',') : 'all',
  ].join('|');
  useEffect(() => {
    setState((current) => (current.phase === 'working' ? current : { phase: 'idle', format: null, message: '' }));
  }, [scopeKey]);

  const working = state.phase === 'working';
  const nothingToExport = empty || (registrationIds != null && registrationIds.length === 0);

  const run = async (format) => {
    const label = FORMATS.find((f) => f.id === format)?.label || format;
    setState({ phase: 'working', format, message: `Preparing the ${label} file…` });
    // Named for what was asked, even if the filters change while it runs.
    const labels = [...scopeLabels];
    try {
      const jobId = await requestParticipantExport(checkInApi, {
        eventIds: [eventId], statuses, search, registrationIds, format,
        fileName: exportTitle(eventName, labels),
      });
      let job = null;
      for (let poll = 0; poll < MAX_POLLS; poll += 1) {
        // eslint-disable-next-line no-await-in-loop
        await wait(pollMs);
        if (!alive.current) return;
        // eslint-disable-next-line no-await-in-loop
        job = await fetchExportJob(checkInApi, jobId);
        if (job.status === 'completed' || job.status === 'failed') break;
      }
      if (!alive.current) return;
      if (!job || (job.status !== 'completed' && job.status !== 'failed')) {
        setState({ phase: 'error', format, message: 'The file is taking longer than usual. Try again in a minute.' });
        return;
      }
      if (job.status === 'failed' || !job.filename) {
        setState({ phase: 'error', format, message: 'The export failed. Try again.' });
        return;
      }
      const blob = await downloadExportFile(checkInApi, job.filename);
      if (!alive.current) return;
      saveBlob(blob, exportFileName(eventName, format, new Date(), labels));
      setState({ phase: 'done', format, message: `${label} file downloaded.` });
    } catch (error) {
      if (!alive.current) return;
      const refused = error?.status === 403;
      setState({
        phase: 'error',
        format,
        message: refused ? 'You don’t have permission to export this list.' : 'The export failed. Try again.',
      });
    }
  };

  return (
    <>
      <div className="ochh-export" role="group" aria-label="Export this list">
        <span className="ochh-exportlab">Export</span>
        {FORMATS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => run(f.id)}
            disabled={disabled || working || nothingToExport}
            aria-busy={working && state.format === f.id}
          >
            {working && state.format === f.id
              ? <i className="bi bi-arrow-clockwise ochk-spin" aria-hidden="true" />
              : <i className={`bi ${f.id === 'csv' ? 'bi-filetype-csv' : 'bi-file-earmark-spreadsheet'}`} aria-hidden="true" />}
            {f.label}
          </button>
        ))}
      </div>
      <p
        className={`ochh-exportmsg${state.phase === 'error' ? ' is-err' : ''}`}
        role="status"
        hidden={!state.message}
      >
        {state.message}
      </p>
    </>
  );
}

export default ExportParticipants;
